import { z } from 'zod';
import { AllowlistRange, Cidr, IpAddress, IsoTime, ServerId } from './basics.js';
import { BotCatalog, BotPattern } from './bots.js';
import { withinAllowlistCoverage } from './command.js';
import { WebEnvSettings } from './settings.js';
import { BlockRules, RequestRuleSet } from './block-rules.js';
import { DomainName } from './web-domain.js';
import { WebRuleFields, WebRules } from './web-rules.js';
export { DomainName } from './web-domain.js';
/**
 * Web environment detection (knowledge doc section 8.8; P5.5).
 *
 * Three shapes, one per boundary:
 *
 * - **`WebInspection`** — what the root helper's read-only `inspect_web_config`
 *   found in the web servers' own configuration. Facts only: names, ports, flags
 *   and addresses. Never configuration text, which can hold credentials
 *   (`proxy_set_header Authorization …`), so rule 5 keeps it on the server.
 * - **`WebEnvironmentProposal`** — what the agent concludes from that and from the
 *   connections it sees, sent to `POST /v1/ingest/web-environment`. A proposal, not
 *   a decision: section 8.8 step 3 and section 20 leave confirmation to an admin
 *   (P6.1), because a wrong trusted list lets attackers choose their own address.
 * - **`ConfigRanges`** — the published ranges the API fetches and hands out in the
 *   config document, so no agent reaches Cloudflare itself.
 */
/** Section 8.8 step 2's modes. */
export const ProxyMode = z.enum([
    'direct',
    'cloudflare',
    'load_balancer',
    'cloudflare_plus_lb',
    'proxy_protocol',
    'unknown',
]);
/** Where the real client address comes from in each proxy mode. */
export const ProxyHeader = z.enum(['CF-Connecting-IP', 'X-Forwarded-For', 'proxy_protocol']);
export const WebServerType = z.enum(['nginx', 'apache', 'other']);
/**
 * `edge` answers visitors and passes some on to a `backend` on the same machine;
 * `standalone` answers them alone.
 */
export const WebServerPosition = z.enum(['edge', 'backend', 'standalone']);
const Port = z.number().int().min(1).max(65535);
/** A header name as a web server spells it in `real_ip_header` or `RemoteIPHeader`. */
export const HeaderName = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/, 'a header name');
/**
 * A name a site answers to. Wildcards and nginx's regular-expression names are
 * kept as written; control characters and spaces never are, since these are shown
 * in React and a newline would forge a line in anything that prints them.
 */
export const SiteName = z
    .string()
    .min(1)
    .max(253)
    .regex(/^[^\p{Cc}\s]+$/u, 'no spaces or control characters');
/** A configuration file, named so an admin can find the site in it. */
export const ConfigPath = z
    .string()
    .startsWith('/')
    .max(512)
    .regex(/^[^\p{Cc}]+$/u, 'no control characters');
/**
 * Real-IP trust a web server already has (`set_real_ip_from` / `real_ip_header`,
 * `RemoteIPTrustedProxy` / `RemoteIPHeader`) — section 8.8's strongest evidence.
 */
export const ExistingRealIp = z.object({
    header: HeaderName.optional(),
    /** Only addresses and ranges, one spelling each (`normalizeCidr`, never the bare address CrowdSec is given). */
    trusted: z.array(Cidr).max(500),
    recursive: z.boolean(),
    /** `real_ip_header proxy_protocol`, or Apache's `RemoteIPProxyProtocol On`. */
    proxyProtocol: z.boolean(),
    /**
     * Trusted entries that were not an address or range — `unix:`, a hostname, a
     * list file — and so could not be compared with anything. Counted so the
     * evidence can say the picture is incomplete.
     */
    ignored: z.number().int().nonnegative(),
});
/** One `listen` or `Listen`, or one address of a `<VirtualHost>`. */
export const WebListen = z.object({
    /** Null for every address (`*`, `0.0.0.0`, `[::]`, or none written). */
    address: z.string().min(1).max(64).nullable(),
    port: Port,
    ssl: z.boolean(),
    proxyProtocol: z.boolean(),
});
export const InspectedSite = z.object({
    /** `file|first name|first port`: stable across edits that do not touch the site. */
    id: z.string().min(1).max(200),
    file: ConfigPath,
    serverNames: z.array(SiteName).max(8),
    listens: z.array(WebListen).max(20),
    /** Section 8.8 step 1: whether the site logs somewhere of its own. */
    hasOwnAccessLog: z.boolean(),
    /** Whether Bartizo's per-site include (P6.1) is already in it. */
    bartizoInclude: z.boolean(),
    realIp: ExistingRealIp.optional(),
});
export const InspectedWebServer = z.object({
    kind: z.enum(['nginx', 'apache']),
    version: z.string().min(1).max(50).optional(),
    /** What `systemctl is-active` said; null when it could not be asked. */
    active: z.boolean().nullable(),
    /** `nginx -T` or `apache2ctl -t` passed. Nothing below is read when it did not. */
    configOk: z.boolean(),
    realIpModule: z.boolean(),
    /** Nginx's Lua module, which the CrowdSec Nginx bouncer needs (section 8.8). */
    luaModule: z.boolean(),
    /**
     * A CrowdSec bouncer is loaded in this web server (P6.6): bans then reach
     * visitors behind a proxy, which a firewall ban cannot. Absent from an older
     * helper's answer, which knew nothing of bouncers.
     */
    bouncer: z.boolean().default(false),
    listens: z.array(WebListen).max(200),
    /** Trust set outside any one site: `http {}`, or Apache's server configuration. */
    realIp: ExistingRealIp.optional(),
    sites: z.array(InspectedSite).max(300),
    /** Anything that makes the picture incomplete: files not read, sites not listed. */
    notes: z.array(z.string().min(1).max(300)).max(10),
});
/** `inspect_web_config`'s answer, in the helper outcome's `data`. */
export const WebInspection = z.object({
    servers: z.array(InspectedWebServer).max(2),
    inspectedAt: IsoTime,
});
/** Read-only, and there is nothing to choose: it inspects what is installed. */
export const InspectWebConfigArgs = z.object({});
export const DetectedSite = z.object({
    id: z.string().min(1).max(200),
    serverNames: z.array(SiteName).max(8),
    ports: z.array(Port).max(20),
    ssl: z.boolean(),
    hasOwnAccessLog: z.boolean(),
    includeInstalled: z.boolean(),
});
export const DetectedWebServer = z.object({
    type: WebServerType,
    /** `nginx`, `apache`, or `unknown` for a listener no configuration claims. */
    name: z.string().min(1).max(50),
    version: z.string().min(1).max(50).optional(),
    position: WebServerPosition,
    ports: z.array(Port).max(50),
    /**
     * False for what Bartizo will not write configuration for: a container,
     * Caddy, Traefik, HAProxy — anything on 80 or 443 that no Nginx or Apache
     * configuration here claims (section 8.8 step 1's "not managed").
     */
    managed: z.boolean(),
    active: z.boolean().nullable(),
    realIpModule: z.boolean(),
    luaModule: z.boolean(),
    sites: z.array(DetectedSite).max(300),
});
/*
 * Domain fronts (P6.7). One address can serve domains whose DNS sends visitors
 * through Cloudflare beside domains it sends straight to the server. The server's
 * mode still configures Nginx — Cloudflare's header is believed only from a
 * Cloudflare peer, so "Through Cloudflare" is right for both kinds — and the front
 * per domain says which visitors to expect where: a direct visitor of a `direct`
 * domain is not somebody going around Cloudflare.
 */
/** What an admin confirms for a domain: visitors arrive through Cloudflare, or directly. */
export const DomainFront = z.enum(['cloudflare', 'direct']);
/**
 * What a domain's DNS answers: every address in Cloudflare's ranges, one of this
 * machine's own, some other address (this machine behind NAT, or another server),
 * or nothing the agent could get.
 */
export const DomainDns = z.enum(['cloudflare', 'direct', 'elsewhere', 'unknown']);
/** At most this many domains are proposed, and looked up, per server. */
export const MAX_PROPOSED_DOMAINS = 100;
/** At most this many fronts are confirmed per server. */
export const MAX_DOMAIN_FRONTS = 300;
const RequestCount = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
export const DetectedDomain = z
    .object({
    name: DomainName,
    dns: DomainDns,
    /** What the DNS answered, so an admin can see why. */
    addresses: z.array(IpAddress).max(8),
    /** Requests for it in Bartizo's access log since the last proposal. */
    requests: RequestCount,
    /** Of those, how many came from a Cloudflare peer. */
    viaCloudflare: RequestCount,
})
    .refine((domain) => domain.viaCloudflare <= domain.requests, {
    message: 'more requests through Cloudflare than requests',
    path: ['viaCloudflare'],
});
/**
 * A `server_name` as a domain: lower case, a leading dot (Nginx's "the name and
 * every subdomain") dropped, a trailing dot dropped. Undefined for what names no
 * one domain: `_`, a wildcard, a regular expression, an address.
 */
export function domainOfServerName(serverName) {
    let name = serverName.trim().toLowerCase();
    if (name.startsWith('.'))
        name = name.slice(1);
    if (name.endsWith('.'))
        name = name.slice(0, -1);
    const parsed = DomainName.safeParse(name);
    // An address matches the pattern; its last label is all digits, a domain's never is.
    if (!parsed.success || /\.\d+$/.test(name))
        return undefined;
    return parsed.data;
}
/**
 * Section 8.8 step 2's proposal (`POST /v1/ingest/web-environment`).
 *
 * `sampledConnections` rather than the contract's `sampledRequests`: until phase
 * 6's access log exists, the agent sees connections (`/proc/net/tcp`), not
 * requests, and a header such as `CF-Ray` is invisible to it.
 */
export const WebEnvironmentProposal = z.object({
    servers: z.array(DetectedWebServer).max(5),
    mode: ProxyMode,
    /** Ranges the proposal would trust for the real client address, besides Cloudflare's. */
    trustedProxies: z.array(Cidr).max(2000),
    /** Trust Cloudflare's distributed list, as `WebEnvironmentSetting` will say it. */
    trustedProxiesRef: z.enum(['cloudflare']).optional(),
    header: ProxyHeader.optional(),
    confidence: z.number().min(0).max(1),
    evidence: z.array(z.string().min(1).max(300)).max(20),
    sampledConnections: z.number().int().nonnegative(),
    /**
     * What the agent put on CrowdSec's `bartizo-proxies` allowlist before anybody
     * confirmed anything (section 21, phase 5). Only from sources an attacker cannot
     * choose — Cloudflare's published ranges, private peers, and the admin's own
     * real-IP configuration — never a public address merely seen connecting.
     */
    allowlisted: z
        .array(AllowlistRange)
        .max(1000)
        .refine((ranges) => withinAllowlistCoverage(ranges), {
        message: 'more than an allowlist may exempt',
    }),
    /** Whether that allowlist is in place in CrowdSec; false when the helper could not set it. */
    allowlistApplied: z.boolean(),
    /** Which of the API's Cloudflare lists the proposal was scored against. */
    rangesVersion: z.string().min(1).max(128).optional(),
    /**
     * Each Nginx site's domain, with what its DNS and its requests say (P6.7).
     * Empty from an agent older than P6.7, and where Nginx names no domain.
     */
    domains: z.array(DetectedDomain).max(MAX_PROPOSED_DOMAINS).default([]),
    detectedAt: IsoTime,
});
/**
 * A site as the helper's parsers name it (`file|first name|first port`), which is
 * how an admin's choice of sites reaches `set_web_integration`.
 */
export const SiteId = z
    .string()
    .min(1)
    .max(200)
    .regex(/^[^\p{Cc}]+$/u, 'no control characters');
/** What an admin can confirm: `unknown` is a proposal's answer, never a setting. */
export const ConfirmedProxyMode = ProxyMode.exclude(['unknown']);
/**
 * Ranges trusted for the real client address. Each no wider than an allowlist
 * entry and sixteen /8s together, because every one of them is also exempted from
 * bans — and trusting `0.0.0.0/0` would let anybody choose their own address.
 */
const TrustedProxies = z
    .array(AllowlistRange)
    .max(500)
    .refine((ranges) => withinAllowlistCoverage(ranges), {
    message: 'more than a trusted proxy list may cover',
});
/**
 * The confirmed web environment (section 8.8 step 3; P6.1): the server's own
 * settings layer holds it as `web.environment`, and only
 * `PUT /v1/servers/:id/web-environment` writes it — admin and a password re-check,
 * section 12's High row, because a wrong trusted list lets attackers fake their
 * address. Its presence is the confirmation; section 20 keeps auto-apply off in v1.
 *
 * `managed_sites` are the Nginx server blocks that get Bartizo's one-line include.
 * Only a site with its own `access_log` needs one: the others inherit the
 * server-wide log Bartizo adds, with no change to their files at all.
 */
export const WebEnvironmentSetting = z
    .object({
    mode: ConfirmedProxyMode,
    trusted_proxies: TrustedProxies.default([]),
    /** Trust the Cloudflare ranges the API distributes (`ConfigDocument.ranges`). */
    trusted_proxies_ref: z.enum(['cloudflare']).optional(),
    header: ProxyHeader.optional(),
    managed_sites: z.array(SiteId).max(300).default([]),
    /**
     * Each domain's front (P6.7), on a server behind Cloudflare that also serves
     * domains directly. Optional, and left out when empty, so a document without
     * any is what it was before P6.7 — an older agent's strict schema refuses a
     * field it does not know.
     */
    domains: z
        .record(DomainName, DomainFront)
        .refine((domains) => Object.keys(domains).length <= MAX_DOMAIN_FRONTS, {
        message: `at most ${String(MAX_DOMAIN_FRONTS)} domains`,
    })
        .optional(),
})
    .strict()
    .superRefine((environment, ctx) => {
    for (const message of environmentProblems(environment)) {
        ctx.addIssue({ code: 'custom', message });
    }
});
const HEADERS_FOR = {
    direct: [],
    cloudflare: ['CF-Connecting-IP', 'X-Forwarded-For'],
    load_balancer: ['X-Forwarded-For'],
    proxy_protocol: ['proxy_protocol'],
    cloudflare_plus_lb: ['X-Forwarded-For', 'CF-Connecting-IP'],
};
/** What each mode needs, in words a person can act on. Empty when it is consistent. */
export function environmentProblems(environment) {
    const { mode, trusted_proxies: trusted, trusted_proxies_ref: ref, header } = environment;
    const problems = [];
    const behindCloudflare = mode === 'cloudflare' || mode === 'cloudflare_plus_lb';
    if (!behindCloudflare && Object.keys(environment.domains ?? {}).length > 0) {
        problems.push(`${mode} sends no visitor through Cloudflare, so every domain is reached the same way; leave domains out`);
    }
    if (behindCloudflare && ref !== 'cloudflare') {
        problems.push(`${mode} trusts Cloudflare's published ranges: set trusted_proxies_ref`);
    }
    if (!behindCloudflare && ref !== undefined) {
        problems.push(`${mode} does not come through Cloudflare, so it trusts no Cloudflare ranges`);
    }
    const needsOwnRanges = mode === 'load_balancer' || mode === 'proxy_protocol' || mode === 'cloudflare_plus_lb';
    if (needsOwnRanges && trusted.length === 0) {
        problems.push(`${mode} needs the balancer's addresses in trusted_proxies`);
    }
    if (!needsOwnRanges && trusted.length > 0) {
        problems.push(`${mode} trusts no addresses of its own; leave trusted_proxies empty`);
    }
    if (header !== undefined && !HEADERS_FOR[mode].includes(header)) {
        problems.push(`${mode} does not read the client address from ${header}`);
    }
    return problems;
}
/** The headers a mode may read the client address from, its default first. */
export const headersFor = (mode) => HEADERS_FOR[mode];
/**
 * The front a confirmed environment gives a request's host (P6.7): its own when
 * confirmed, undefined when the environment names no fronts or not this host. The
 * host is the log's `$host` — lower case already, without a port.
 */
export function domainFrontOf(environment, host) {
    const domains = environment?.domains;
    if (domains === undefined)
        return undefined;
    const name = host.toLowerCase().replace(/\.$/, '');
    return Object.hasOwn(domains, name) ? domains[name] : undefined;
}
/** Whether a confirmed environment serves domains both through Cloudflare and directly. */
export const hasDirectDomains = (environment) => Object.values(environment?.domains ?? {}).includes('direct');
/** The header a confirmed environment reads the client address from, if any. */
export function environmentHeader(environment) {
    return environment.header ?? HEADERS_FOR[environment.mode][0];
}
/**
 * `web` in a settings layer and a config document: the confirmed environment
 * (P6.1) and the bot rules (P6.6, `web-rules.ts`).
 */
export const WebSettings = z
    .object({
    environment: WebEnvironmentSetting.optional(),
    ...WebRuleFields,
    /**
     * Block rules (P11.5, `block-rules.ts`). Optional and left out when empty, so
     * a document without any is what it was before — an older agent's strict
     * schema refuses a field it does not know.
     */
    block_rules: BlockRules.optional(),
})
    .strict();
/**
 * Real-IP trust for `set_web_integration`, already resolved: the agent turns
 * `trusted_proxies_ref` into Cloudflare's ranges, so the helper writes exactly
 * what it is given.
 */
export const WebIntegrationRealIp = z
    .object({
    header: ProxyHeader,
    trusted: z
        .array(AllowlistRange)
        .min(1)
        .max(1000)
        .refine((ranges) => withinAllowlistCoverage(ranges), {
        message: 'more than a trusted proxy list may cover',
    }),
})
    .strict();
/**
 * `set_web_integration` (section 8.8; P6.1): make one web server's Bartizo
 * configuration exactly this. The whole desired state, not a change — so it is
 * safe to repeat (rule 8), a file edited by hand is put back, and nothing at all
 * (`real_ip: null`, `log: false`, no sites) removes everything Bartizo wrote.
 *
 * No text reaches a configuration file from here except addresses the schema has
 * proved to be ranges and header names from a fixed list.
 */
export const SetWebIntegrationArgs = z
    .object({
    server: z.enum(['nginx', 'apache']),
    /** Null: Bartizo trusts no forwarding header on this server. */
    real_ip: WebIntegrationRealIp.nullable(),
    /** Bartizo's JSON access log, for phase 6's traffic module. */
    log: z.boolean(),
    /** Nginx server blocks with their own `access_log` that should log to Bartizo too. */
    sites: z.array(SiteId).max(300),
    /**
     * Log a request once: leave out the requests whose TCP peer is this machine
     * itself, the second hop of a front web server block passing to another on
     * loopback (P11.18). Sent only when true, so a helper older than P11.18 still
     * takes the rest.
     */
    skip_internal_hop: z.boolean().optional(),
    /** Only say whether anything differs; write nothing (P8.3's alert-only drift). */
    dry_run: z.boolean().optional(),
})
    .strict();
/** What `set_web_integration` did, in the helper outcome's `data`. */
export const WebIntegrationResult = z.object({
    /** False when everything was already as asked: nothing was written or reloaded. */
    changed: z.boolean(),
    /** Files written, linked or removed. */
    files: z.array(ConfigPath).max(700),
    /**
     * `kept_existing`: the server already had real-IP settings of the admin's own,
     * which Bartizo never overwrites; it only adds its log beside them.
     */
    realIp: z.enum(['written', 'kept_existing', 'none']),
    reloaded: z.boolean(),
    notes: z.array(z.string().min(1).max(300)).max(10),
});
/**
 * `set_web_block_rules` (section 12; P6.6): make one web server's bot blocks
 * exactly this — 403 for a user agent containing any `blocked` pattern and none
 * of the `allowed` ones — and, from P11.5, its request rules. The whole desired state: nothing blocked removes what
 * Bartizo wrote. Patterns are literal (`BotPattern`) and written escaped.
 *
 * `sites`: the Nginx server blocks an admin let Bartizo add its marked line to
 * (the confirmed environment's `managed_sites`), which is where Nginx needs the
 * rule. Apache applies it everywhere from one file, and ignores this.
 */
export const SetWebBlockRulesArgs = z
    .object({
    server: z.enum(['nginx', 'apache']),
    blocked: z.array(BotPattern).max(2000),
    allowed: z.array(BotPattern).max(2000),
    sites: z.array(SiteId).max(300),
    /**
     * Request rules (P11.5): refused by path and method, per site. Optional and
     * sent only when there are some, so a helper older than P11.5 still takes
     * the bot blocks.
     */
    requests: RequestRuleSet.optional(),
    /** Only say whether anything differs; write nothing (P8.3's alert-only drift). */
    dry_run: z.boolean().optional(),
})
    .strict();
/** A zone per rule, named from what it matches (`zoneNameFor`). */
export const RateLimitZone = z.string().regex(/^bz_rl_[a-z0-9_]{1,80}$/, 'a Bartizo zone name');
export const WebRateLimit = z
    .object({
    zone: RateLimitZone,
    patterns: z.array(BotPattern).min(1).max(1000),
    per_minute: z.number().int().min(1).max(6000),
    burst: z.number().int().min(0).max(1000),
})
    .strict();
/**
 * `set_web_rate_limits` (section 12; P6.6): Nginx's `limit_req` per rule, keyed
 * by the restored client address, 429 past the burst. The whole desired state,
 * like the blocks. Apache has no request limiting of its own and refuses.
 */
export const SetWebRateLimitsArgs = z
    .object({
    server: z.enum(['nginx', 'apache']),
    limits: z
        .array(WebRateLimit)
        .max(20)
        .refine((limits) => new Set(limits.map((limit) => limit.zone)).size === limits.length, {
        message: 'each zone once',
    }),
    allowed: z.array(BotPattern).max(2000),
    /** Only say whether anything differs; write nothing (P8.3's alert-only drift). */
    dry_run: z.boolean().optional(),
})
    .strict();
/** What either rules operation did, in the helper outcome's `data`. */
export const WebRulesResult = z.object({
    /** False when everything was already as asked: nothing was written or reloaded. */
    changed: z.boolean(),
    files: z.array(ConfigPath).max(700),
    reloaded: z.boolean(),
    notes: z.array(z.string().min(1).max(300)).max(10),
});
export const WebEnvironmentReceipt = z.object({
    serverId: ServerId,
    mode: ProxyMode,
    receivedAt: IsoTime,
});
/**
 * Published ranges in the config document (section 8.8: "sent to agents in the
 * config document"). Crawler ranges for bot verification join in phase 6.
 *
 * `version` identifies the list, not when it was fetched: a fetch that finds the
 * same list must not mint every server a new settings version each day.
 */
export const ConfigRanges = z.object({
    /** Cloudflare's list only: it is what `web_env` looks again for when it changes. */
    version: z.string().min(1).max(128),
    cloudflare: z.array(AllowlistRange).max(500),
    /**
     * Crawler operators' published lists (P6.4), by the URL the bot catalog names,
     * for `web_traffic`'s verification. Only the lists the API has fetched: a list
     * that is not here makes its bots fall back to reverse DNS, or `unknown`.
     */
    crawlers: z
        .record(z.url(), z.array(AllowlistRange).max(5000))
        .refine((lists) => Object.keys(lists).length <= 50, { message: 'at most 50 lists' })
        .optional(),
});
export const PublishedRangesOrigin = z.enum(['vendored', 'fetched']);
/** How the API's copy of a published list stands, for a person wondering why it is old. */
export const PublishedRangesStatus = z.object({
    source: z.string().min(1).max(64),
    version: z.string().min(1).max(128),
    count: z.number().int().nonnegative(),
    origin: PublishedRangesOrigin,
    fetchedAt: IsoTime.nullable(),
    checkedAt: IsoTime.nullable(),
    lastError: z.string().max(500).nullable(),
});
/**
 * Something the traffic shows that the detected environment does not explain (P11.17):
 * the access log's addresses against the confirmed or proposed setup. A hint is
 * advice, never a setting: it says what was seen and what to look at, and the
 * confirmation stays an admin's, with a password re-check.
 */
export const WebEnvironmentHintCode = z.enum([
    'loopback_hop',
    'private_peers',
    'cloudflare_visible',
    'dominant_address',
]);
export const WebEnvironmentHint = z.object({
    code: WebEnvironmentHintCode,
    level: z.enum(['notice', 'warning']),
    title: z.string().min(1).max(200),
    detail: z.string().min(1).max(1000),
    /** Requests it is about, over the last day, and their share of all. */
    requests: z.number().int().nonnegative(),
    share: z.number().min(0).max(1),
    /** The addresses behind it, the busiest first. */
    addresses: z.array(z.string().max(64)).max(5),
});
/** `GET /v1/servers/:id/web-environment`, and the answer to `PUT` and `DELETE`. */
export const ServerWebEnvironment = z.object({
    serverId: ServerId,
    proposal: WebEnvironmentProposal.nullable(),
    receivedAt: IsoTime.nullable(),
    /** What an admin confirmed (P6.1), from the server's own settings layer. */
    confirmed: WebEnvironmentSetting.nullable(),
    cloudflare: PublishedRangesStatus.nullable(),
    /** What the last day's access log shows that the setup does not explain. */
    hints: z.array(WebEnvironmentHint).max(10).default([]),
});
/**
 * What the agent's `web_env` module is handed (`moduleSlices`): its settings, the
 * Cloudflare ranges the API put in the document (`ConfigDocument.ranges`), which
 * it scores peers against and allowlists, and the confirmed environment it makes
 * the web servers match (P6.1). Here rather than beside `WebEnvSettings`, because
 * `settings.ts` importing this file would close a cycle through `command.ts`.
 */
export const WebEnvModuleSettings = WebEnvSettings.extend({
    cloudflare: z.array(AllowlistRange).max(500).default([]),
    ranges_version: z.string().min(1).max(128).optional(),
    environment: WebEnvironmentSetting.optional(),
    /** The bot blocks and rate limits it makes the web servers enforce (P6.6). */
    rules: WebRules.default({}),
    /** The block rules (P11.5): request rules for the web servers, behaviour rules for CrowdSec. */
    block_rules: BlockRules.default([]),
    /** The API's catalog, which names what the rules' bot ids mean when it is newer. */
    bot_catalog: BotCatalog.optional(),
});
//# sourceMappingURL=web.js.map